import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../App";
import { useWidgetMode } from "@/lib/widgetMode";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Lock, Clock, TrendingUp, ChevronRight, Info, HelpCircle, BarChart3, Zap, Users, Target } from "lucide-react";
import VehicleHistoryTile from "../components/VehicleHistoryTile";
import VehicleRecallTile from "../components/VehicleRecallTile";

// Confidence Factor Component
const ConfidenceFactor = ({ icon: Icon, label, description, score, detail }) => {
  const maxScore = 25;
  const percentage = (score / maxScore) * 100;
  
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
        <Icon className="w-4 h-4 text-slate-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-slate-900">{label}</span>
          <span className="text-xs text-slate-500">{score}/{maxScore}</span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-1.5 mb-1">
          <div 
            className={`h-1.5 rounded-full transition-all duration-500 ${
              percentage >= 80 ? 'bg-emerald-500' : 
              percentage >= 60 ? 'bg-blue-500' : 
              percentage >= 40 ? 'bg-amber-500' : 'bg-red-500'
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <p className="text-xs text-slate-500">{detail}</p>
      </div>
    </div>
  );
};

const InstantOfferScreen = ({ state, updateState }) => {
  const navigate = useNavigate();
  const { offer, vehicleInfo } = state;
  const [showPrice, setShowPrice] = useState(false);
  const [showConfidenceDetails, setShowConfidenceDetails] = useState(false);
  const { isCompact: compact } = useWidgetMode();

  useEffect(() => {
    if (!offer) {
      navigate('/');
      return;
    }
    // Trigger price reveal animation
    const timer = setTimeout(() => setShowPrice(true), 300);
    return () => clearTimeout(timer);
  }, [offer, navigate]);

  if (!offer) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      maximumFractionDigits: 0 
    }).format(value);
  };

  const confidenceGrade = offer.breakdown?.confidence?.grade || 'B';
  const validDate = new Date(offer.valid_until).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });

  const fieldHeight = compact ? "h-11" : "h-14";

  return (
    <div className={compact ? "flex flex-col bg-white" : "min-h-screen flex flex-col bg-white"}>
      <Header showProgress currentStep={3} variant="light" />

      <main className={compact ? "py-4" : "min-h-[calc(100vh-80px)] py-12 sm:py-20"}>
        <div className="container-flow">
          {/* Success Badge */}
          <div className={`text-center opacity-0 animate-fade-in-up ${compact ? "mb-4" : "mb-8"}`}>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
              <TrendingUp className="w-4 h-4" />
              Offer Generated Successfully
            </span>
          </div>

          {/* Offer Card */}
          <div className={`surface-elevated text-center opacity-0 animate-fade-in-up animate-stagger-2 ${compact ? "p-4" : "p-8 sm:p-12"}`}>
            {/* Vehicle Info */}
            <p className="text-slate-600 mb-2">Your cash offer for</p>
            <h2 className={compact ? "text-lg font-medium text-slate-900 mb-3" : "text-heading-3 mb-6"}>
              {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
            </h2>

            {/* Price Reveal */}
            <div className={`${compact ? "py-3" : "py-8"} ${showPrice ? 'animate-price-reveal' : 'opacity-0'}`}>
              <p className="offer-price" data-testid="instant-offer-price">
                {formatCurrency(offer.instant_offer)}
              </p>
              <p className="text-slate-500 mt-2">
                Range: {formatCurrency(offer.offer_range_low)} – {formatCurrency(offer.offer_range_high)}
              </p>
            </div>

            {/* Confidence Score */}
            <div className={`flex flex-col items-center gap-3 ${compact ? "mb-4" : "mb-8"}`}>
              <div className="flex items-center gap-3">
                <div className={`confidence-badge grade-${confidenceGrade.toLowerCase()}`}>
                  <span className="mr-1">Confidence:</span>
                  <span className="font-semibold">{offer.confidence_score}/100</span>
                  <span className="ml-1">(Grade {confidenceGrade})</span>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button 
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                        data-testid="confidence-tooltip-trigger"
                      >
                        <HelpCircle className="w-5 h-5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs p-4 bg-slate-900 text-white">
                      <p className="font-semibold mb-2">What does this mean?</p>
                      <p className="text-sm text-slate-300 mb-3">
                        Our confidence score reflects how accurately we can price your vehicle based on current market data.
                      </p>
                      <div className="space-y-1 text-xs">
                        <p><span className="text-emerald-400 font-medium">Grade A (90+):</span> Excellent data, highly accurate</p>
                        <p><span className="text-blue-400 font-medium">Grade B (65-89):</span> Good data, reliable estimate</p>
                        <p><span className="text-amber-400 font-medium">Grade C (45-64):</span> Moderate data, reasonable estimate</p>
                        <p><span className="text-red-400 font-medium">Grade D (&lt;45):</span> Limited data, wider range</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              
              <button 
                onClick={() => setShowConfidenceDetails(!showConfidenceDetails)}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                data-testid="show-confidence-details"
              >
                <Info className="w-4 h-4" />
                {showConfidenceDetails ? 'Hide' : 'See'} what factors into this score
              </button>
              
              {/* Confidence Breakdown Panel */}
              {showConfidenceDetails && offer.breakdown?.confidence?.breakdown && (
                <div className="w-full max-w-md bg-slate-50 rounded-xl p-4 mt-2 text-left animate-fade-in-up">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-3">Confidence Factors</p>
                  <div className="space-y-3">
                    {offer.breakdown.confidence.breakdown.comp_count !== undefined && (
                      <ConfidenceFactor 
                        icon={BarChart3}
                        label="Market Data"
                        description="Comparable vehicles found"
                        score={offer.breakdown.confidence.breakdown.comp_count}
                        detail={`${offer.comps_used} similar vehicles analyzed`}
                      />
                    )}
                    {offer.breakdown.confidence.breakdown.market_liquidity !== undefined && (
                      <ConfidenceFactor 
                        icon={Target}
                        label="Market Coverage"
                        description="How concentrated the market is"
                        score={offer.breakdown.confidence.breakdown.market_liquidity}
                        detail="Based on geographic spread"
                      />
                    )}
                    {offer.breakdown.confidence.breakdown.market_velocity !== undefined && (
                      <ConfidenceFactor 
                        icon={Zap}
                        label="Market Activity"
                        description="How quickly vehicles are selling"
                        score={offer.breakdown.confidence.breakdown.market_velocity}
                        detail={`${offer.breakdown.tradepending?.average_days_on_market || '—'} avg days on market`}
                      />
                    )}
                    {offer.breakdown.confidence.breakdown.demand !== undefined && (
                      <ConfidenceFactor 
                        icon={Users}
                        label="Demand"
                        description="Buyer interest in this vehicle"
                        score={offer.breakdown.confidence.breakdown.demand}
                        detail="Based on market demand signals"
                      />
                    )}
                  </div>
                </div>
              )}
              
              <p className="text-sm text-slate-500">
                Based on {offer.comps_used} comparable vehicles
              </p>
            </div>

            {/* Valid Until */}
            <div className={`flex items-center justify-center gap-2 text-slate-600 ${compact ? "mb-4" : "mb-8"}`}>
              <Clock className="w-5 h-5" />
              <span>Valid until {validDate}</span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col @sm:flex-row gap-4 max-w-md mx-auto">
              <Button
                onClick={() => navigate('/condition')}
                className={`flex-1 ${fieldHeight} btn-accent animate-pulse-glow`}
                data-testid="lock-my-offer-btn"
              >
                <Lock className="w-5 h-5 mr-2" />
                Lock My Offer
              </Button>
            </div>

            <p className="text-sm text-slate-500 mt-4">
              Answer a few questions about your vehicle's condition to lock in your final offer.
            </p>
          </div>

          {/* Vehicle History Report */}
          <div className={`opacity-0 animate-fade-in-up animate-stagger-3 ${compact ? "mt-3" : "mt-6"}`}>
            <VehicleHistoryTile vin={state.vin} />
          </div>

          {/* Vehicle Recall Check */}
          <div className={`opacity-0 animate-fade-in-up animate-stagger-4 ${compact ? "mt-3" : "mt-4"}`}>
            <VehicleRecallTile vin={state.vin} vehicleInfo={vehicleInfo} offerId={offer?.offer_id} />
          </div>

          {/* Offer Details Breakdown */}
          <div className={`surface-card opacity-0 animate-fade-in-up animate-stagger-5 ${compact ? "mt-3 p-4" : "mt-6 p-6"}`}>
            <button 
              className="w-full flex items-center justify-between text-left"
              onClick={() => {}}
            >
              <span className="font-medium text-slate-900">View Offer Details</span>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
            
            <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Mileage</span>
                <span className="font-medium">{parseInt(state.mileage).toLocaleString()} miles</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">ZIP Code</span>
                <span className="font-medium">{state.zipCode}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Comparable Vehicles Used</span>
                <span className="font-medium">{offer.comps_used}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Offer ID</span>
                <span className="font-mono text-xs">{offer.offer_id.slice(0, 8)}...</span>
              </div>
            </div>
          </div>

          {/* What's Next */}
          <div className={`bg-slate-50 rounded-2xl opacity-0 animate-fade-in-up animate-stagger-6 ${compact ? "mt-3 p-4" : "mt-6 p-6"}`}>
            <h3 className="font-semibold text-slate-900 mb-3">What happens next?</h3>
            <ol className="space-y-2 text-sm text-slate-600">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0" style={{ backgroundColor: 'hsl(220, 70%, 15%)' }}>1</span>
                Answer questions about your vehicle's condition
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0" style={{ backgroundColor: 'hsl(220, 70%, 15%)' }}>2</span>
                Upload photos of your vehicle
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0" style={{ backgroundColor: 'hsl(220, 70%, 15%)' }}>3</span>
                Receive your locked-in final offer
              </li>
            </ol>
          </div>
        </div>
      </main>
      {!compact && <Footer minimal />}
    </div>
  );
};

export default InstantOfferScreen;
